import cx from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { useAsyncDebounce } from 'react-table';
import NativeSelect from '@material-ui/core/NativeSelect';
import useLocalStorage from '../lib/useLocalStorage';
import TextInput from './TextInput';
import { useSaleTable } from './SaleTable';

const filterConfigs = [
  {
    column: 'price',
    name: 'Price',
  },
  {
    column: 'nameLength',
    name: 'Name length',
    className: 'tw-col-span-1',
  },
  {
    column: 'tld',
    className: 'tw-col-span-1 sm:tw-col-span-2 md:tw-col-span-1',
    name: 'Extensions',
  },
  {
    name: 'Other options',
    className:
      'tw-col-span-1 sm:tw-col-span-2 md:tw-col-span-1 lg:tw-col-span-1 xl:tw-col-span-2',
    facets: [
      {
        column: 'noNumbers',
      },
      {
        column: 'onlyNumbers',
      },
      {
        column: 'noHyphens',
      },
      {
        column: 'nsfw',
      },
    ],
  },
];

export default function SaleTableToolbar({ hiddenFilters = [] }) {
  const history = useHistory();
  const location = useLocation();
  const [showFilters, setShowFilters] = useLocalStorage(
    'SaleTable:showFilters',
    false,
  );
  const {
    allColumns,
    loading,
    rowHeight,
    setAllFilters,
    setGlobalFilter,
    setRowHeight,
    setPageSize,
    totalItems,
    state: { filters, globalFilter, pageSize },
  } = useSaleTable();
  const setGlobalFilterDebounced = useAsyncDebounce(
    value => setGlobalFilter(value),
    200,
  );
  return (
    <div className="tw-py-5">
      <div className="tw-relative">
        <div className="tw-flex tw-items-center">
          <button
            className={cx(
              {
                'tw-paper': !showFilters,
              },
              'tw-group tw-relative tw-box-content tw-z-10 tw-inline-flex tw-items-center tw-justify-center tw-w-20 tw-h-12 tw-pl-2 tw-pr-4 tw-font-bold tw-rounded-lg focus:tw-outline-none focus-visible:tw-ring-2',
            )}
            onClick={() => setShowFilters(!showFilters)}
            type="button">
            <i
              className={cx(
                'tw-text-2xl tw-text-secondary group-hover:tw-text-secondary-dark tw-mr-2 gb-icon',
                {
                  'gb-icon-morpheus-minus-full': showFilters,
                  'gb-icon-morpheus-plus-circle': !showFilters,
                },
              )}
            />
            Filter
          </button>
          <div className="tw-flex tw-flex-wrap tw-flex-auto sm:tw-flex-none tw-ml-4 tw-whitespace-nowrap">
            <div className="tw-text-sm sm:tw-text-base tw-mr-3">
              {loading
                ? 'loading...'
                : Number.isInteger(totalItems)
                ? `${totalItems.toLocaleString()} domains`
                : ''}
            </div>
            {!loading && filters.length > 0 && (
              <button
                className="tw-inline-flex tw-items-center tw-text-sm tw-text-secondary tw-group"
                onClick={() => setAllFilters([])}>
                <i className="gb-icon gb-icon-morpheus-cross-small" />
                <span className="tw-ml-1 tw-lowercase group-hover:tw-underline tw-whitespace-nowrap">
                  Clear filters
                </span>
              </button>
            )}
          </div>
        </div>
        {showFilters && (
          <div className="tw-filters">
            <div
              className="tw-grid md:tw-grid-cols-2 lg:tw-grid-cols-4 xl:tw-grid-cols-5 tw-bg-gray-100 tw-rounded-lg tw-overflow-hidden"
              style={{ gap: '1px' }}>
              {filterConfigs
                .filter(filter => hiddenFilters.indexOf(filter.column) === -1)
                .map(filter => (
                  <div
                    className={cx(
                      filter.className,
                      'tw-px-5 tw-py-5 sm:tw-py-6 md:tw-py-8 tw-bg-white',
                    )}
                    key={filter.name}>
                    <div className="tw-text-sm">{filter.name}</div>
                    <div className="tw-mt-2">
                      {filter.facets
                        ? filter.facets.map(f => (
                            <div
                              className="tw-flex md:tw-inline-flex tw-items-center tw-mr-4 tw-my-2 lg:tw-my-1"
                              key={f.column}>
                              {allColumns
                                .find(column => column.id === f.column)
                                .render('Filter')}
                            </div>
                          ))
                        : allColumns
                            .find(column => column.id === filter.column)
                            .render('Filter')}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
        <div className="md:tw-absolute tw-top-0 tw-right-0 tw-flex tw-items-center tw-mt-5 md:tw-mt-0">
          <div className="tw-flex-auto">
            <TextInput
              className="md:tw-w-56 tw-mr-5"
              defaultValue={globalFilter}
              inputClassName="tw-pr-6"
              innerRight={
                <i className="gb-icon gb-icon-morpheus-search tw-mx-2" />
              }
              onChange={event => setGlobalFilterDebounced(event.target.value)}
              placeholder="Search"
              size="small"
            />
          </div>
          <div className="tw-flex tw-items-center tw-justify-end">
            <div className="tw-hidden tw-relative md:tw-flex tw-px-1 tw-rounded tw-mr-1 hover:tw-bg-gray-100">
              <i
                className="gb-icon gb-icon-list tw-text-2xl tw-relative"
                style={{ top: '-1px' }}
              />
              <select
                className="tw-cursor-pointer tw-absolute tw-inset-0 tw-w-full tw-opacity-0"
                onChange={event => setRowHeight(event.target.value)}
                title="Toggle row spacing"
                value={rowHeight}>
                <option disabled>Row height</option>
                <option value="default">Comfortable (default)</option>
                <option value="compact">Compact</option>
              </select>
            </div>
            <NativeSelect
              classes={{ root: 'tw-h-full tw-px-1' }}
              className="tw-h-8 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-appearance-none tw-rounded tw-overflow-hidden hover:tw-bg-gray-100 tw-text-right"
              onChange={event => {
                setPageSize(Number(event.target.value));
                history.push({
                  ...location,
                  state: { ...location.state, pageSize: event.target.value },
                });
              }}
              inputProps={{
                title: 'Toggle rows per page',
              }}
              style={{ width: pageSize < 100 ? '3rem' : '3.5rem' }}
              value={pageSize}>
              <option disabled>Rows per page</option>
              {[10, 25, 50, 100].map(option => (
                <option key={option}>{option}</option>
              ))}
            </NativeSelect>
          </div>
        </div>
      </div>
    </div>
  );
}
